import { z } from 'zod';

/**
 * All specific auth error types our stack can throw or catch.
 *
 * Some are thrown by backend Better-Auth hooks (ORGANIZATION_NOT_FOUND,
 * RECRUITER_NOT_FOUND) and arrive via URL redirect for display on /auth/error.
 * Others are inferred client-side by the auth layout hooks to classify
 * failures and decide UX response. Every value is handled by the frontend.
 */
export const AUTH_ERRORS = {
  ORGANIZATION_NOT_FOUND: 'ORGANIZATION_NOT_FOUND',
  RECRUITER_NOT_FOUND: 'RECRUITER_NOT_FOUND',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR',
} as const;

export const AuthError = z.enum(AUTH_ERRORS);
export type AuthError = z.infer<typeof AuthError>;

/**
 * Static paths for auth-related pages in the dashboard app.
 * Prefer these constants over hardcoded strings so renames stay in sync.
 */
export const AUTH_PATHS = {
  SIGN_IN: '/auth/sign-in',
  ERROR: '/auth/error',
  /** Safe landing page for the dashboard when a callbackUrl is missing/invalid. */
  DASHBOARD_HOME: '/staff-iq',
  /** Safe landing page for the Chrome extension iframe when a callbackUrl is missing/invalid. */
  EXTENSION_HOME: '/chrome/toro-extension',
} as const;

/** Any route under this prefix is served inside the Chrome extension. */
export const EXTENSION_PATH_PREFIX = '/chrome/';

/**
 * Query param used to signal that a sign-in flow originated from the extension.
 * Survives even if `callbackUrl` is dropped or corrupted, so AutoRedirect can
 * keep the user scoped to extension paths regardless.
 *
 * TODO: remove once the extension has its own dedicated sign-in page —
 * this belt-and-suspenders signal exists only because the extension currently
 * shares the dashboard sign-in route.
 */
export const EXTENSION_SOURCE_PARAM = 'source';
export const EXTENSION_SOURCE_VALUE = 'extension';
